/*
 * Copyright (c) 2008-2020 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */

import Vue from "vue"
import api from "@/config/api"
import { defaultUserProperties } from "@/config/userPropertyConstants"

export default {
  namespaced: true,

  state: {
    promise: null,
    userId: "",
    tenantId: null,
    properties: Object.values(defaultUserProperties).reduce((result, key) => {
      // Initialize all known properties to ensure components and templates
      // do not attempt to operate on transient values.
      result[key] = {
        type: "STRING",
        value: "",
      }
      return result
    }, {}),
  },

  mutations: {
    /**
     * Commits promise for loading data to local store
     *
     * @param state
     * @param promise
     */
    storePromise(state, promise) {
      state.promise = promise
    },

    /**
     * Commits userId to local store
     *
     * @param {Object} state
     * @param {string} userId
     */
    updateUserId(state, userId) {
      state.userId = userId
    },

    /**
     * Commits tenantId to local store
     *
     * @param {Object} state
     * @param {string} tenantId
     */
    updateTenantId(state, tenantId) {
      state.tenantId = tenantId
    },

    /**
     * Commits name information to local store
     *
     * @param {Object} state
     * @param {string} username
     */
    updateFullName(state, { salutation, title, firstname, lastname }) {
      state.properties[defaultUserProperties.SALUTATION] = salutation
      state.properties[defaultUserProperties.TITLE] = title
      state.properties[defaultUserProperties.FIRST_NAME] = firstname
      state.properties[defaultUserProperties.LAST_NAME] = lastname
    },

    /**
     * Commits username to local store
     *
     * @param {Object} state
     * @param {string} username
     */
    updateUsername(state, username) {
      state.properties[defaultUserProperties.USER_NAME] = username
    },

    /**
     * Commits email to local store
     *
     * @param {Object} state
     * @param {string} email
     */
    updateEmail(state, email) {
      state.properties[defaultUserProperties.EMAIL] = email
    },

    /**
     * Commits hide for Interaction Onboarding to local store
     * @description Hint: hiddenModals is a comma separates list (Array), of modal names, which should no longer be displayed.
     * @param {Object} state
     * @param {string} hiddenModals
     */
    updateHideTourModals(state, hiddenModals) {
      Vue.set(state.properties, defaultUserProperties.HIDE_TOUR_MODALS, hiddenModals)
    },

    /**
     * Commits active sort to local store
     *
     * @param state
     * @param activeMeetingSortOption
     */
    updateActiveMeetingSortOption(state, activeMeetingSortOption) {
      state.properties[defaultUserProperties.ACTIVE_MEETING_SORT_OPTION] = activeMeetingSortOption
    },

    /**
     * Commits active meeting filter to local store
     *
     * @param state
     * @param meetingFilterConfig
     */
    updateMeetingFilterConfig(state, meetingFilterConfig) {
      state.properties[defaultUserProperties.MEETING_FILTER_CONFIG] = meetingFilterConfig
    },
  },

  actions: {
    /**
     * Requests currently logged-in user's information.
     *
     * @param state
     * @param commit
     * @returns {Promise<any>}
     */
    loadInfo({ state, commit }) {
      let promise = new Promise((resolve, reject) => {
        Vue.http.get(api.ME()).then(
          (response) => {
            // Load tenant info only for users associated to a tenant
            if (response.body.tenantId) {
              commit("updateTenantId", response.body.tenantId)
              // IF we're logged in as a tenant-user, OVERWRITE the tenantFilterId.
              //  Pending: We REALLY shouldn't misuse the tenantFilterId for this! We have a separate field "tenantId"!
              commit(
                "tenants/setTenantFilterId",
                { tenantId: response.body.tenantId, saveInWebStorage: false },
                { root: true }
              )
            }
            commit("updateUserId", response.body.userId)
            commit("updateFullName", {
              salutation: response.body.properties[defaultUserProperties.SALUTATION] || { type: "STRING", value: "" },
              title: response.body.properties[defaultUserProperties.TITLE] || { type: "STRING", value: "" },
              firstname: response.body.properties[defaultUserProperties.FIRST_NAME] || { type: "STRING", value: "" },
              lastname: response.body.properties[defaultUserProperties.LAST_NAME] || { type: "STRING", value: "" },
            })
            commit(
              "updateUsername",
              response.body.properties[defaultUserProperties.USER_NAME] || { type: "STRING", value: "" }
            )
            commit(
              "updateEmail",
              response.body.properties[defaultUserProperties.EMAIL] || { type: "STRING", value: "" }
            )
            commit(
              "updateHideTourModals",
              response.body.properties[defaultUserProperties.HIDE_TOUR_MODALS] || {
                type: "STRING",
                value: "",
              }
            )
            commit(
              "updateActiveMeetingSortOption",
              response.body.properties[defaultUserProperties.ACTIVE_MEETING_SORT_OPTION] || {
                type: "STRING",
                value: "",
              }
            )
            commit(
              "updateMeetingFilterConfig",
              response.body.properties[defaultUserProperties.MEETING_FILTER_CONFIG] || { type: "STRING", value: "" }
            )
            resolve(response)
          },
          (error) => {
            commit("auth/authError", null, { root: true })
            if (error.status === 401) reject({ type: "credentialsError" })
            else if (error.status === 0) reject({ type: "networkError" })
            else reject({ type: "serverError" })
          }
        )
      })
      commit("storePromise", promise)
      return state.promise
    },

    /**
     * Attempts to update the user settings stored on the server.
     * On success updates local store
     *
     * @param {Object} param0
     * @param {Object} settings
     * @returns {Promise}
     */
    update({ state, commit }, settings) {
      let body = {}
      // Either copy over the property provided or provide an initial value for a complete object
      for (const key of Object.values(defaultUserProperties)) {
        // Flattens the data structure before sending to ensure format the api expects
        if (settings?.[key]) {
          body[key] = String(settings[key]?.value)
        } else {
          body[key] = state.properties[key].value
        }
      }

      return new Promise((resolve, reject) => {
        Vue.http.put(api.ME(), body).then(
          (response) => {
            commit("updateFullName", {
              salutation: response.body.properties[defaultUserProperties.SALUTATION] || { type: "STRING", value: "" },
              title: response.body.properties[defaultUserProperties.TITLE] || { type: "STRING", value: "" },
              firstname: response.body.properties[defaultUserProperties.FIRST_NAME] || { type: "STRING", value: "" },
              lastname: response.body.properties[defaultUserProperties.LAST_NAME] || { type: "STRING", value: "" },
            })
            commit(
              "updateUsername",
              response.body.properties[defaultUserProperties.USER_NAME] || { type: "STRING", value: "" }
            )
            commit(
              "updateEmail",
              response.body.properties[defaultUserProperties.EMAIL] || { type: "STRING", value: "" }
            )
            commit(
              "updateHideTourModals",
              response.body.properties[defaultUserProperties.HIDE_TOUR_MODALS] || {
                type: "STRING",
                value: "",
              }
            )
            commit(
              "updateActiveMeetingSortOption",
              response.body.properties[defaultUserProperties.ACTIVE_MEETING_SORT_OPTION] || {
                type: "STRING",
                value: "",
              }
            )
            commit(
              "updateMeetingFilterConfig",
              response.body.properties[defaultUserProperties.MEETING_FILTER_CONFIG] || { type: "STRING", value: "" }
            )
            resolve(response.body)
          },
          (response) => {
            reject(response)
          }
        )
      })
    },

    /**
     * Attempts to update the user settings stored on the server.
     * On success updates local store
     *
     * @param {Object} param0
     * @param {Object} settings
     * @returns {Promise}
     */
    // eslint-disable-next-line no-empty-pattern
    updatePassword({}, settings) {
      return new Promise((resolve, reject) => {
        Vue.http.put(api.ME_PASSWORD(), { password: settings.password }).then(
          (response) => {
            resolve(response.data)
          },
          (response) => {
            reject(response)
          }
        )
      })
    },
  },

  getters: {
    /**
     * get user's configured settings
     *
     * @returns {Object}
     */
    userSettings: (state) => ({
      id: state.userId,
      tenantId: state.tenantId,
      properties: Object.values(defaultUserProperties).reduce((result, key) => {
        result[key] = state.properties[key]
        return result
      }, {}),
    }),

    /**
     * A promise that resolves when user settings have been loaded. Resolves immediately, if settings already
     * finished loading.
     *
     * Hint: Loading is triggered by OAuth2AuthorizationCode.js before emitting "OAuth:login:finished" event. Otherwise,
     * the promise should be returned by an action that triggers loading, if not yet in progress.
     *
     * @param state
     * @param getters
     * @returns {Promise<unknown>}
     */
    userSettingsPromise: (state, getters) => {
      return new Promise((resolve) => {
        let promise = state.promise
        promise.then(() => resolve(getters.userSettings))
      })
    },
  },
}
